import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Camera, MapPin, X } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { useAuth } from '../hooks/useAuth';
import { useBreadcrumb } from '../hooks/useBreadcrumb';
import Feed from '../components/home/Feed';
import ClimbMasonryGrid from '../components/home/ClimbMasonryGrid';
import FollowingCarousel from '../components/home/FollowingCarousel';
import { PushNudgeCard } from '../components/push/PushNudgeCard';
import { FeatureFlagToggle } from '../components/feature-flag-toggle';
import { featureFlagsService } from '../services/feature-flags';

const DISMISS_KEY = 'home_new_homepage_flag_dismissed';

export default function ClimbsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  useBreadcrumb(
    () => ({
      onBack: () => navigate(-1),
      segments: [{ label: t('climbs.pageTitle') }],
    }),
    [t, navigate]
  );

  const [hasNoFollowings, setHasNoFollowings] = useState(false);
  const handleCarouselEmpty = useCallback(() => setHasNoFollowings(true), []);

  const [newHomepageFlagDismissed, setNewHomepageFlagDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === 'true',
  );
  const dismissNewHomepageFlag = () => {
    localStorage.setItem(DISMISS_KEY, 'true');
    setNewHomepageFlagDismissed(true);
  };

  const showAddAvatar = !user?.avatar_url;
  const showFindGym = hasNoFollowings;

  return (
    <div className="flex flex-col gap-6">
      <PushNudgeCard placement="climbs" />

      {showAddAvatar && (
        <Alert
          className="cursor-pointer border-orange-500/50 bg-orange-500/10"
          onClick={() => navigate('/profile')}
        >
          <Camera className="h-4 w-4 text-orange-500" />
          <AlertTitle>{t('home.profileCompletion.addAvatar.title')}</AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span>{t('home.profileCompletion.addAvatar.description')}</span>
            <Button
              variant="link"
              className="p-0 h-auto text-orange-600"
              onClick={(e) => {
                e.stopPropagation();
                navigate('/profile');
              }}
            >
              {t('home.profileCompletion.addAvatar.cta')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {showFindGym && (
        <Alert
          className="cursor-pointer border-green-500/50 bg-green-500/10"
          onClick={() => navigate('/search')}
        >
          <MapPin className="h-4 w-4 text-green-600" />
          <AlertTitle>{t('home.profileCompletion.findGym.title')}</AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span>{t('home.profileCompletion.findGym.description')}</span>
            <Button
              variant="link"
              className="p-0 h-auto text-green-700"
              onClick={(e) => {
                e.stopPropagation();
                navigate('/search');
              }}
            >
              {t('home.profileCompletion.findGym.cta')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div className="w-full flex flex-col">
        {!newHomepageFlagDismissed && !user?.feature_flags?.new_homepage && (
          <div className="relative max-w-sm">
            <FeatureFlagToggle
              title={t('featureFlags.newHomepage.title')}
              description={t('featureFlags.newHomepage.description')}
              enabled={user?.feature_flags?.new_homepage ?? false}
              onToggle={(enabled) =>
                featureFlagsService.toggleFlag('new_homepage', enabled).then(() => {
                  window.location.reload();
                })
              }
            />
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 h-6 w-6 text-muted-foreground hover:text-foreground"
              onClick={dismissNewHomepageFlag}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
        <FollowingCarousel onEmpty={handleCarouselEmpty} />
        {user?.feature_flags?.new_homepage ? <ClimbMasonryGrid /> : <Feed />}
      </div>
    </div>
  );
}
